import { agreementSigned, withAiConsentWithdrawn, withClientAiConsent, type V2Agreement } from '../domain/agreement';
import { AREA_KIND_LABELS, type AreaKind } from '../domain/areaCatalog';
import { conditionalTemplateRowById, templateRowsFor } from '../domain/checklistTemplates';
import { conditionalRowsDue, type DescriptorKey } from '../domain/descriptors';
import { areaSeedsFromPlan, areaSectionOf, emptyHousePlan, suggestedAreaName, type HousePlan, type PlanNaming } from '../domain/housePlan';
import { alignAgreementToService, withItemReportSentence, type ItemReportSentence, areaCanComplete, areaCompletionBlockers, areaCompletionFor, completedInspection, createArea, createInspection, createItem, finalizedInspection, inspectionCanComplete, isInspectionLocked, isInspectionMovedOut, progressFor, purgedInspection, reopenedInspection, touchedInspection, withAgreement, withEvidenceSuggestionDismissed, withEvidenceSuggestions, withEvidenceLink, withItemNote, withItemStructuralConcern, withoutEvidenceEntirely, withItemReason, withItemReasonCode, withItemStatus, withLimitedPortion, withLimitedPortionAt, withoutEvidenceLink, withoutLimitedPortion, canCarryLimitedPortions, withPropertyJurisdiction, withPropertyFacts, withItemDescriptor, withReportLanguage, withClientChineseSummary, withScopeSnapshot, withStartMode, type AreaCompletionSummary, type InspectionPurge, type ItemStatus, type LimitedPortion, type NewInspectionInput, type ProgressSummary, type PropertyJurisdictionPatch, type PropertyFactsPatch, type ReportLanguage, type StartMode, type UnableReason, type V2Area, type V2ChecklistItem, type V2Inspection, type AreaNameLabel } from '../domain/inspection';
import { scopeSnapshotForAgreement } from '../domain/scopeGate';
import type { DefectSeverity } from '../domain/severity';
import { putRecords, type RecordDelete, type RecordWrite } from '../persistence/v2Database';
import { createStore } from './createStore';
import { currentInspectorProfile, inspectorRefFrom } from './preferences';
import { assertAgreementSignable, assertFormalWorkflowEligible, formalEligibilityBlockedFor, formalWorkflowRefusalFor, type FormalWorkflowRefusal } from './formalWorkflow';

// Inspection / area / checklist state. Hydrated exclusively from the yanwushi-v2 database
// (src/state/hydrate.ts) — there is no legacy read path here by design (owner ruling
// 2026-09-01, NO AUTOMATIC LEGACY IMPORT).
//
// Write discipline: every action builds ONE list of record writes, persists it in ONE
// IndexedDB transaction, and only then updates in-memory state. Completion is reconciled
// after every mutation (an area with an unchecked row can never stay "completed"; an
// inspection with an incomplete enabled area can never stay "completed") — so state changes
// always recompute progress, and nothing is ever locked — EXCEPT after the final report was
// generated: a finalized inspection is read-only (Owner task book 2026-09-04 §18), enforced by
// assertInspectionUnlocked in every mutating action of every store.
export interface InspectionState {
  hydrated: boolean;
  inspections: V2Inspection[];
  areas: V2Area[];
  items: V2ChecklistItem[];
  droppedRecords: number;
  upgradedRecords: number;
}

const emptyState: InspectionState = {
  hydrated: false,
  inspections: [],
  areas: [],
  items: [],
  droppedRecords: 0,
  upgradedRecords: 0
};

export const inspectionStore = createStore<InspectionState>(emptyState);

export function resetInspectionStoreForTests(): void {
  inspectionStore.set(() => ({ ...emptyState, inspections: [], areas: [], items: [] }));
}

export function applyInspectionSnapshot(snapshot: {
  inspections: V2Inspection[];
  areas: V2Area[];
  items: V2ChecklistItem[];
  dropped: number;
  upgraded: number;
}): void {
  inspectionStore.set(() => ({
    hydrated: true,
    inspections: sortInspections(snapshot.inspections),
    areas: snapshot.areas,
    items: snapshot.items,
    droppedRecords: snapshot.dropped,
    upgradedRecords: snapshot.upgraded
  }));
}

// ---------- finalization lock ----------

export class InspectionLockedError extends Error {
  readonly inspectionId: string;
  constructor(inspectionId: string) {
    super(`inspection ${inspectionId} is finalized and read-only`);
    this.name = 'InspectionLockedError';
    this.inspectionId = inspectionId;
  }
}

// Locked for writes: finalized (§18), or — Owner 2026-10-20 — exported to a file and carried to another
// device, where the work goes on.
export function isInspectionLockedById(state: InspectionState, inspectionId: string): boolean {
  const inspection = state.inspections.find((i) => i.id === inspectionId);
  return inspection ? isInspectionLocked(inspection) || isInspectionMovedOut(inspection) : false;
}

// Called by every mutating action of every store before it writes. Unknown ids pass (the write
// path decides what to do with a missing parent).
export function assertInspectionUnlocked(inspectionId: string): void {
  if (isInspectionLockedById(inspectionStore.get(), inspectionId)) throw new InspectionLockedError(inspectionId);
}

// ---------- selectors ----------

export function areasOf(state: InspectionState, inspectionId: string): V2Area[] {
  return state.areas.filter((a) => a.inspectionId === inspectionId).sort((a, b) => a.sortOrder - b.sortOrder);
}

export function itemsOf(state: InspectionState, areaId: string): V2ChecklistItem[] {
  return state.items.filter((i) => i.areaId === areaId).sort((a, b) => a.sortOrder - b.sortOrder);
}

export function areaProgress(state: InspectionState, areaId: string): ProgressSummary {
  return progressFor(state.items.filter((i) => i.areaId === areaId));
}

// Inspection-wide progress counts only enabled areas (disabled ones are out of scope).
//
// NY F1 Round 5 (OD-12): an inspection whose working data was deleted by its final report has no
// rows left to count, so the counts FROZEN at generation stand in for them. Every list, progress bar
// and monthly total in the app therefore keeps telling the truth about a delivered inspection
// without keeping a single result, note or photo of it.
export function inspectionProgress(state: InspectionState, inspectionId: string): ProgressSummary {
  const purge = state.inspections.find((i) => i.id === inspectionId)?.purge ?? null;
  if (purge) return purge.progress;
  const enabledAreaIds = new Set(state.areas.filter((a) => a.inspectionId === inspectionId && a.enabled).map((a) => a.id));
  return progressFor(state.items.filter((i) => enabledAreaIds.has(i.areaId)));
}

// Enabled / completed areas of one inspection, purge-aware for the same reason.
export function inspectionAreaCompletion(state: InspectionState, inspectionId: string): AreaCompletionSummary {
  const purge = state.inspections.find((i) => i.id === inspectionId)?.purge ?? null;
  if (purge) return purge.areas;
  return areaCompletionFor(state.areas.filter((a) => a.inspectionId === inspectionId));
}

// Inspection order = enabled areas by sortOrder, their items by sortOrder. "Next Item →" (D-06) and
// "Resume Inspection" (§6.5) use it: the next Uninspected item after `afterItemId` (wrapping around
// to earlier items), or the first one; null when nothing is left.
export function nextUninspectedItem(state: InspectionState, inspectionId: string, afterItemId: string | null = null): V2ChecklistItem | null {
  const ordered: V2ChecklistItem[] = [];
  for (const area of areasOf(state, inspectionId).filter((a) => a.enabled)) ordered.push(...itemsOf(state, area.id));
  if (ordered.length === 0) return null;
  const anchor = afterItemId ? ordered.findIndex((i) => i.id === afterItemId) : -1;
  const start = anchor >= 0 ? anchor + 1 : 0;
  for (let step = 0; step < ordered.length; step += 1) {
    const candidate = ordered[(start + step) % ordered.length];
    if (candidate.status === 'notChecked' && candidate.id !== afterItemId) return candidate;
  }
  return null;
}

// ---------- internals ----------

function sortInspections(list: readonly V2Inspection[]): V2Inspection[] {
  return [...list].sort((a, b) => b.updatedAt - a.updatedAt || a.id.localeCompare(b.id));
}

function replaceById<T extends { id: string }>(list: readonly T[], next: readonly T[]): T[] {
  const byId = new Map(next.map((n) => [n.id, n] as const));
  const seen = new Set<string>();
  const out = list.map((existing) => {
    const replacement = byId.get(existing.id);
    if (replacement) seen.add(existing.id);
    return replacement ?? existing;
  });
  for (const n of next) if (!seen.has(n.id)) out.push(n);
  return out;
}

interface Mutation {
  inspections: V2Inspection[];
  areas: V2Area[];
  items: V2ChecklistItem[];
}

function emptyMutation(): Mutation {
  return { inspections: [], areas: [], items: [] };
}

// Reconciles completion for one inspection against the state that WILL exist after `m`.
// Adds the corrective records to `m` (area reopen, inspection reopen) and the touch.
function reconcile(m: Mutation, inspectionId: string, now: number): void {
  const state = inspectionStore.get();
  const areas = replaceById(state.areas, m.areas).filter((a) => a.inspectionId === inspectionId);
  const items = replaceById(state.items, m.items).filter((i) => i.inspectionId === inspectionId);
  const fixedAreas: V2Area[] = [];
  for (const area of areas) {
    if (area.completedAt !== null && !areaCanComplete(items.filter((i) => i.areaId === area.id))) {
      fixedAreas.push({ ...area, completedAt: null, updatedAt: now });
    }
  }
  if (fixedAreas.length > 0) m.areas = replaceById(m.areas, fixedAreas);
  const finalAreas = replaceById(areas, fixedAreas);

  const currentInspection = m.inspections.find((i) => i.id === inspectionId) ?? state.inspections.find((i) => i.id === inspectionId);
  if (!currentInspection) return;
  let next = touchedInspection(currentInspection, now);
  if (next.status === 'completed' && !inspectionCanComplete(finalAreas)) next = reopenedInspection(next, now);
  m.inspections = replaceById(m.inspections, [next]);
}

async function commit(m: Mutation, deletes: readonly RecordDelete[] = []): Promise<void> {
  const writes: RecordWrite[] = [
    ...m.inspections.map((value) => ({ store: 'inspections' as const, value })),
    ...m.areas.map((value) => ({ store: 'areas' as const, value })),
    ...m.items.map((value) => ({ store: 'items' as const, value }))
  ];
  await putRecords(writes, deletes);
  inspectionStore.set((prev) => ({
    ...prev,
    inspections: sortInspections(replaceById(prev.inspections, m.inspections)),
    areas: replaceById(prev.areas, m.areas),
    items: replaceById(prev.items, m.items)
  }));
}

// Lets other stores (findings, evidence) touch the inspection inside THEIR transaction.
export function prepareInspectionTouch(inspectionId: string, now: number): { write: RecordWrite | null; apply: () => void } {
  const current = inspectionStore.get().inspections.find((i) => i.id === inspectionId);
  if (!current) return { write: null, apply: () => undefined };
  const next = touchedInspection(current, now);
  return {
    write: { store: 'inspections', value: next },
    apply: () =>
      inspectionStore.set((prev) => ({ ...prev, inspections: sortInspections(replaceById(prev.inspections, [next])) }))
  };
}

// Lets another store (the cover-photo lane) apply an inspection record it already persisted.
export function upsertInspectionLocal(next: V2Inspection): void {
  inspectionStore.set((prev) => ({ ...prev, inspections: sortInspections(replaceById(prev.inspections, [next])) }));
}

// NY F1 Round 5 (OD-12): the finalize write AND the purge of the working data, prepared for the ONE
// transaction that stores the last PDF (src/state/purgeInspection.ts is the only caller). The record
// that stays is the basic-information row of the approved draft §5.3; every area and checklist row
// of the inspection goes with the rest of the working data.
//
// This is now the ONLY way an inspection becomes finalized: there is no lock-without-purge seam, so
// a future caller cannot generate a final report and leave the working data behind.
export function prepareInspectionPurge(inspectionId: string, purge: InspectionPurge, now: number): { write: RecordWrite | null; areaIds: string[]; itemIds: string[]; apply: () => void } {
  const state = inspectionStore.get();
  const areaIds = state.areas.filter((a) => a.inspectionId === inspectionId).map((a) => a.id);
  const itemIds = state.items.filter((i) => i.inspectionId === inspectionId).map((i) => i.id);
  const current = state.inspections.find((i) => i.id === inspectionId);
  if (!current) return { write: null, areaIds, itemIds, apply: () => undefined };
  const next = purgedInspection(finalizedInspection(current, now), purge, now);
  return {
    write: { store: 'inspections', value: next },
    areaIds,
    itemIds,
    apply: () =>
      inspectionStore.set((prev) => ({
        ...prev,
        inspections: sortInspections(replaceById(prev.inspections, [next])),
        areas: prev.areas.filter((a) => a.inspectionId !== inspectionId),
        items: prev.items.filter((i) => i.inspectionId !== inspectionId)
      }))
  };
}

function seedArea(
  inspectionId: string,
  kind: AreaKind,
  name: string,
  floor: number | null,
  label: AreaNameLabel | null,
  sortOrder: number,
  now: number
): { area: V2Area; items: V2ChecklistItem[] } {
  const area = createArea(inspectionId, kind, name, sortOrder, now, floor, label);
  const items = templateRowsFor(kind).map((row, index) =>
    createItem(inspectionId, area.id, { templateId: row.id, title: row.title, hint: row.hint }, index, now)
  );
  return { area, items };
}

// NY F1 Round 2 (frozen plan §13): the three CONDITIONAL rows (CK-02 fuel shut-off · CK-03 fixed
// supplementary heat · CK-07 foundation insulation / ventilation) appear in an EXISTING enabled area
// of their kind the moment their trigger fact is recorded — once, appended at the end, never for a
// house whose facts do not call for them. A row is formal field-work content, so a blocked
// inspection (R1 eligibility) gets none until its block is answered; a row is never removed when a
// trigger later changes (rows are never deleted — the inspector marks it N/A).
function seedConditionalRows(m: Mutation, inspectionId: string, now: number): void {
  const state = inspectionStore.get();
  const inspection = m.inspections.find((i) => i.id === inspectionId) ?? state.inspections.find((i) => i.id === inspectionId);
  // ELIGIBILITY, not X-1: creation already seeds the plan's rows onto a booked-but-unsigned
  // inspection, so a fact recorded at setup seeds its conditional row the same way. Field work
  // still waits for the signature (src/state/formalWorkflow.ts).
  if (!inspection || formalEligibilityBlockedFor(inspection)) return;
  const areas = replaceById(state.areas, m.areas).filter((a) => a.inspectionId === inspectionId && a.enabled);
  const items = replaceById(state.items, m.items).filter((i) => i.inspectionId === inspectionId);
  for (const due of conditionalRowsDue({ property: inspection.property, items })) {
    if (items.some((i) => i.templateId === due.templateId)) continue;
    const row = conditionalTemplateRowById(due.templateId);
    const area = areas.filter((a) => a.kind === due.kind).sort((a, b) => a.sortOrder - b.sortOrder)[0];
    if (!row || !area) continue;
    const areaItems = items.filter((i) => i.areaId === area.id);
    const sortOrder = areaItems.length > 0 ? Math.max(...areaItems.map((i) => i.sortOrder)) + 1 : 0;
    const item = createItem(inspectionId, area.id, { templateId: row.id, title: row.title, hint: row.hint }, sortOrder, now);
    m.items.push(item);
    items.push(item);
  }
}

// ---------- inspection actions ----------

// Creation seeds one Area record (with its checklist rows) per instance of the house plan —
// the plan is consumed here and never stored (src/domain/housePlan.ts). The plan is ALWAYS the
// caller's: the New Inspection form passes exactly what the inspector set (Round 2 §D — nothing is
// seeded silently; a form without any layout yields only the whole-house systems). Tests and
// development scenarios pass their own plan explicitly; the fallback is the EMPTY plan.
export async function createInspectionAction(input: NewInspectionInput, now: number = Date.now(), plan: HousePlan = emptyHousePlan(), naming?: PlanNaming): Promise<V2Inspection> {
  // NY F1 Round 4: an inspection created with an ALREADY SIGNED agreement (an internal seam — the
  // development scenarios and tests) is signed at that moment, so its scope snapshot is taken here;
  // the production form always creates an unsigned agreement and is snapshotted at signing below.
  // 2026-10-19 (Owner item 50, L-4): an inspection created without inspector details records the
  // account's bound licence holder, exactly as the New Inspection form does.
  const created = createInspection(input.inspector ? input : { ...input, inspector: inspectorRefFrom(currentInspectorProfile(), '') }, now);
  const inspection = withScopeSnapshot(created, scopeSnapshotForAgreement(created, now), now);
  const m = emptyMutation();
  m.inspections.push(inspection);
  areaSeedsFromPlan(plan, naming).forEach((seed, index) => {
    const seeded = seedArea(inspection.id, seed.kind, seed.name, seed.floor, seed.label, index, now);
    m.areas.push(seeded.area);
    m.items.push(...seeded.items);
  });
  // NY F1 Round 2: facts given at setup may already call for a conditional row.
  seedConditionalRows(m, inspection.id, now);
  await commit(m);
  return inspection;
}

// Seeds a house plan into an inspection that has NO areas yet (Property Layout "Use Suggested
// Layout", Authority §6.4). Refuses when any area record exists — the plan is never a second
// authority over recorded areas.
export async function applyHousePlanAction(inspectionId: string, plan: HousePlan, naming?: PlanNaming, now: number = Date.now()): Promise<V2Area[]> {
  assertInspectionUnlocked(inspectionId);
  const state = inspectionStore.get();
  const inspection = state.inspections.find((i) => i.id === inspectionId);
  if (!inspection || areasOf(state, inspectionId).length > 0) return [];
  // NY F1 Round 1 (§15.1 effect 1): "ordinary NY workflow cannot start (no area seeding, no
  // checklist under the NY authority)". Seeding IS the start of the formal workflow, so it is
  // refused here at the store — a screen-level guard would not stop a programmatic caller.
  assertFormalWorkflowEligible(inspection);
  const m = emptyMutation();
  areaSeedsFromPlan(plan, naming).forEach((seed, index) => {
    const seeded = seedArea(inspectionId, seed.kind, seed.name, seed.floor, seed.label, index, now);
    m.areas.push(seeded.area);
    m.items.push(...seeded.items);
  });
  reconcile(m, inspectionId, now);
  await commit(m);
  return m.areas;
}

export interface InspectionDetailsPatch {
  property?: Partial<V2Inspection['property']>;
  client?: Partial<V2Inspection['client']>;
  inspector?: Partial<V2Inspection['inspector']>;
  weather?: string;
  notes?: string;
  inspectionAt?: number;
}

export async function updateInspectionDetailsAction(
  inspectionId: string,
  patch: InspectionDetailsPatch,
  now: number = Date.now()
): Promise<V2Inspection | null> {
  assertInspectionUnlocked(inspectionId);
  const current = inspectionStore.get().inspections.find((i) => i.id === inspectionId);
  if (!current) return null;
  const next: V2Inspection = touchedInspection(
    // OD-9: an occupancy correction here changes WHICH document applies, so the same re-opening rule
    // as the jurisdiction seam holds (alignAgreementToService).
    alignAgreementToService({
      ...current,
      property: { ...current.property, ...patch.property },
      client: { ...current.client, ...patch.client },
      inspector: { ...current.inspector, ...patch.inspector },
      weather: patch.weather ?? current.weather,
      notes: patch.notes ?? current.notes,
      inspectionAt: patch.inspectionAt ?? current.inspectionAt
    }),
    now
  );
  const m = emptyMutation();
  m.inspections.push(next);
  await commit(m);
  return next;
}

// NY F1 Round 1 (OC-1): the inspector's one-time answers to the jurisdiction / PP-20 questions.
// Nothing else on the record is touched — no photo, note, result, area or agreement — and an
// answer the inspector did not give is never written.
export async function setPropertyJurisdictionAction(
  inspectionId: string,
  patch: PropertyJurisdictionPatch,
  now: number = Date.now()
): Promise<V2Inspection | null> {
  assertInspectionUnlocked(inspectionId);
  const current = inspectionStore.get().inspections.find((i) => i.id === inspectionId);
  if (!current) return null;
  const next = touchedInspection(withPropertyJurisdiction(current, patch, now), now);
  const m = emptyMutation();
  m.inspections.push(next);
  // NY F1 Round 2: an answer that unblocks the formal workflow lets the conditional rows catch up.
  seedConditionalRows(m, inspectionId, now);
  if (m.items.length > 0) reconcile(m, inspectionId, now);
  await commit(m);
  return m.inspections.find((i) => i.id === inspectionId) ?? next;
}

// NY F1 Round 2 (CK-01 / 197-5.8(d) · 197-5.10(a)(1)): the property facts, answered once by the
// inspector — at setup or later on the details screen for a record written before the field existed.
// Only what was answered is written; an explicit null clears an answer. A recorded fuel may make a
// conditional row due (plan §13).
export async function setPropertyFactsAction(inspectionId: string, patch: PropertyFactsPatch, now: number = Date.now()): Promise<V2Inspection | null> {
  assertInspectionUnlocked(inspectionId);
  const current = inspectionStore.get().inspections.find((i) => i.id === inspectionId);
  if (!current) return null;
  const next = withPropertyFacts(current, patch, now);
  const m = emptyMutation();
  if (next !== current) m.inspections.push(touchedInspection(next, now));
  seedConditionalRows(m, inspectionId, now);
  if (m.inspections.length === 0 && m.items.length === 0) return current;
  reconcile(m, inspectionId, now);
  await commit(m);
  return m.inspections.find((i) => i.id === inspectionId) ?? current;
}

// NY F1 Round 2 (OD-1 = A1): one descriptor answer on one row, through the same single write path as
// every other item edit (lock respected; completion reconciled). Never blocks area completion. A
// recorded fuel / heat-source answer may make a conditional row due (plan §13).
export async function setItemDescriptorAction(itemId: string, key: DescriptorKey, value: string, detail: string = '', now: number = Date.now()): Promise<V2ChecklistItem | null> {
  const current = inspectionStore.get().items.find((i) => i.id === itemId);
  if (!current) return null;
  assertInspectionUnlocked(current.inspectionId);
  const next = withItemDescriptor(current, key, value, detail, now);
  const m = emptyMutation();
  if (next !== current) m.items.push(next);
  seedConditionalRows(m, current.inspectionId, now);
  if (m.items.length === 0) return current;
  reconcile(m, current.inspectionId, now);
  await commit(m);
  return next;
}

// The Inspection Agreement is part of the inspection record and editable until finalization.
// NY F1 Round 5: saving it also stamps WHICH document was signed (OD-9, withAgreement) and the
// approved text version the signatures were given under (withSignatures), so a signed agreement can
// always be reprinted exactly as it was signed.
export async function updateAgreementAction(inspectionId: string, agreement: V2Agreement, now: number = Date.now()): Promise<V2Inspection | null> {
  assertInspectionUnlocked(inspectionId);
  const current = inspectionStore.get().inspections.find((i) => i.id === inspectionId);
  if (!current) return null;
  // NY F1 Round 1 (§15.1 effect 3 / §7.3 "No NY report, no NY agreement"): while the inspection is
  // blocked, the ordinary New York Agreement is not executed — the 197-4.2(a) clauses must not
  // stand as a signed home-inspection contract for a property the product has determined it must
  // not inspect. ONLY reaching the SIGNED state is refused: the fee, the Demo flag and the names
  // stay editable (each of those edits re-opens the document anyway), so the record is never a dead
  // end and the inspector can always correct whatever caused the block.
  // ELIGIBILITY only (X-1): the unsigned state is exactly what this action exists to leave.
  if (agreementSigned(agreement)) assertAgreementSignable(current);
  const withDocument = touchedInspection(withAgreement(current, agreement, now), now);
  // NY F1 Round 4 (plan §5.4; runtime contract §6): THIS is the moment the New York scope is frozen
  // for the inspection — the dispositions that the signed agreement, the property facts and the
  // authority version in force produce. Re-opening the document (a fee or Demo change re-opens it)
  // clears the snapshot again: a scope snapshot only ever describes a signed agreement.
  const next = withScopeSnapshot(withDocument, scopeSnapshotForAgreement(withDocument, now), now);
  const m = emptyMutation();
  m.inspections.push(next);
  // A signature may unblock the formal workflow, which lets a conditional row catch up (plan §13).
  seedConditionalRows(m, inspectionId, now);
  if (m.items.length > 0) reconcile(m, inspectionId, now);
  await commit(m);
  return m.inspections.find((i) => i.id === inspectionId) ?? next;
}

// The client's consent tick on the agreement (OD-8 / agreement v2 §1). Voluntary: it never blocks
// signing, and changing it on a signed agreement re-opens the document so the client signs what the
// box now says (src/domain/agreement.ts withClientAiConsent).
export async function setClientAiConsentAction(inspectionId: string, consent: boolean, now: number = Date.now()): Promise<V2Inspection | null> {
  assertInspectionUnlocked(inspectionId);
  const current = inspectionStore.get().inspections.find((i) => i.id === inspectionId);
  if (!current) return null;
  const agreement = withClientAiConsent(current.agreement, consent);
  if (agreement === current.agreement) return current;
  if (agreementSigned(agreement)) assertAgreementSignable(current);
  const withDocument = touchedInspection(withAgreement(current, agreement, now), now);
  // Ticking the box re-opens a signed document, so the Round 4 scope snapshot follows the document:
  // frozen while it is signed, cleared the moment it is not.
  const next = withScopeSnapshot(withDocument, scopeSnapshotForAgreement(withDocument, now), now);
  const m = emptyMutation();
  m.inspections.push(next);
  await commit(m);
  return next;
}

// A written withdrawal of that consent before the report is generated (agreement v2 §1 last bullet).
// The signed agreement stands — only the AI path closes — so field work is never interrupted by it.
export async function withdrawAiConsentAction(inspectionId: string, now: number = Date.now()): Promise<V2Inspection | null> {
  assertInspectionUnlocked(inspectionId);
  const current = inspectionStore.get().inspections.find((i) => i.id === inspectionId);
  if (!current) return null;
  const agreement = withAiConsentWithdrawn(current.agreement, now);
  if (agreement === current.agreement) return current;
  // The signed agreement stands (the snapshot with it); only the AI path closes.
  const next = touchedInspection(withAgreement(current, agreement, now), now);
  const m = emptyMutation();
  m.inspections.push(next);
  await commit(m);
  return next;
}

export async function setStartModeAction(inspectionId: string, startMode: StartMode | null, now: number = Date.now()): Promise<V2Inspection | null> {
  assertInspectionUnlocked(inspectionId);
  const current = inspectionStore.get().inspections.find((i) => i.id === inspectionId);
  if (!current) return null;
  const next = withStartMode(current, startMode, now);
  if (next === current) return current;
  const m = emptyMutation();
  m.inspections.push(next);
  await commit(m);
  return next;
}

// Report Language is chosen on the Report screen, per inspection, until the report is generated.
export async function setReportLanguageAction(inspectionId: string, reportLanguage: ReportLanguage, now: number = Date.now()): Promise<V2Inspection | null> {
  assertInspectionUnlocked(inspectionId);
  const current = inspectionStore.get().inspections.find((i) => i.id === inspectionId);
  if (!current) return null;
  const next = withReportLanguage(current, reportLanguage, now);
  if (next === current) return current;
  const m = emptyMutation();
  m.inspections.push(next);
  await commit(m);
  return next;
}

// The client's Chinese summary (Owner item 23) is switched on the Report screen, until generation.
export async function setClientChineseSummaryAction(inspectionId: string, on: boolean, now: number = Date.now()): Promise<V2Inspection | null> {
  assertInspectionUnlocked(inspectionId);
  const current = inspectionStore.get().inspections.find((i) => i.id === inspectionId);
  if (!current) return null;
  const next = withClientChineseSummary(current, on, now);
  if (next === current) return current;
  const m = emptyMutation();
  m.inspections.push(next);
  await commit(m);
  return next;
}

export async function completeInspectionAction(
  inspectionId: string,
  now: number = Date.now()
): Promise<{ ok: true } | { ok: false; reason: 'notFound' | 'areasIncomplete' | 'locked' | FormalWorkflowRefusal }> {
  const state = inspectionStore.get();
  const current = state.inspections.find((i) => i.id === inspectionId);
  if (!current) return { ok: false, reason: 'notFound' };
  if (isInspectionLocked(current)) return { ok: false, reason: 'locked' };
  // The last status transition before the deliverable. An inspection the product has determined it
  // must not inspect is not completed by calling this directly either.
  const refusal = formalWorkflowRefusalFor(current);
  if (refusal) return { ok: false, reason: refusal };
  if (!inspectionCanComplete(areasOf(state, inspectionId))) return { ok: false, reason: 'areasIncomplete' };
  const m = emptyMutation();
  m.inspections.push(completedInspection(current, now));
  await commit(m);
  return { ok: true };
}

export async function reopenInspectionAction(inspectionId: string, now: number = Date.now()): Promise<void> {
  assertInspectionUnlocked(inspectionId);
  const current = inspectionStore.get().inspections.find((i) => i.id === inspectionId);
  if (!current) return;
  const m = emptyMutation();
  m.inspections.push(reopenedInspection(current, now));
  await commit(m);
}

// Called by the delete cascade (src/state/deleteInspection.ts) after the DB transaction.
export function removeInspectionLocal(inspectionId: string): void {
  inspectionStore.set((prev) => ({
    ...prev,
    inspections: prev.inspections.filter((i) => i.id !== inspectionId),
    areas: prev.areas.filter((a) => a.inspectionId !== inspectionId),
    items: prev.items.filter((i) => i.inspectionId !== inspectionId)
  }));
}

// ---------- area actions ----------

export interface NewAreaInput {
  kind: AreaKind;
  // Instance name; empty = next free "<floor><kind label> n" (src/domain/housePlan.ts).
  name?: string;
  floor?: number | null;
  // Language-neutral descriptor of a preset instance the inspector left unnamed (suggestedAreaInstance),
  // so the Report Language re-expresses the name like a plan-seeded room; absent / null = inspector-named,
  // printed as written (2026-10-18: before this every added area was stored inspector-named and the
  // English client report of a zh-CN inspection printed 「卧室」).
  label?: AreaNameLabel | null;
}

// Adds ONE more area instance with its own checklist rows — a fourth bedroom, a second
// kitchen, a detached garage — at any time during the inspection.
export async function addAreaAction(inspectionId: string, input: NewAreaInput, now: number = Date.now()): Promise<V2Area> {
  assertInspectionUnlocked(inspectionId);
  const state = inspectionStore.get();
  // Same rule as applyHousePlanAction: one more area is one more NY checklist. Fail-closed — an
  // inspection that cannot be resolved is not eligible either.
  assertFormalWorkflowEligible(state.inspections.find((i) => i.id === inspectionId) ?? null);
  const existing = areasOf(state, inspectionId);
  const floor = input.floor ?? null;
  const trimmed = (input.name ?? '').trim();
  const finalName = trimmed.length > 0 ? trimmed : suggestedAreaName(AREA_KIND_LABELS[input.kind], floor, existing);
  const sortOrder = existing.length > 0 ? Math.max(...existing.map((a) => a.sortOrder)) + 1 : 0;
  const seeded = seedArea(inspectionId, input.kind, finalName, floor, input.label ?? null, sortOrder, now);
  const m = emptyMutation();
  m.areas.push(seeded.area);
  m.items.push(...seeded.items);
  reconcile(m, inspectionId, now);
  await commit(m);
  return seeded.area;
}

async function updateArea(areaId: string, mutate: (area: V2Area) => V2Area, now: number): Promise<V2Area | null> {
  const current = inspectionStore.get().areas.find((a) => a.id === areaId);
  if (!current) return null;
  assertInspectionUnlocked(current.inspectionId);
  const next = { ...mutate(current), updatedAt: now };
  const m = emptyMutation();
  m.areas.push(next);
  reconcile(m, next.inspectionId, now);
  await commit(m);
  return m.areas.find((a) => a.id === areaId) ?? next;
}

export function setAreaEnabledAction(areaId: string, enabled: boolean, now: number = Date.now()) {
  return updateArea(areaId, (a) => ({ ...a, enabled }), now);
}

export function renameAreaAction(areaId: string, name: string, now: number = Date.now()) {
  const trimmed = name.trim();
  // A real rename makes the area inspector-named: the plan label is dropped so the new name prints as is.
  return updateArea(areaId, (a) => (trimmed.length > 0 && trimmed !== a.name ? { ...a, name: trimmed, label: null } : a), now);
}

export function setAreaNoteAction(areaId: string, note: string, now: number = Date.now()) {
  return updateArea(areaId, (a) => ({ ...a, note }), now);
}

// Area overall note for the AI scan (区域整体备注, Round 2 §H) — persisted whether or not AI runs.
export function setAreaScanNoteAction(areaId: string, scanNote: string, now: number = Date.now()) {
  return updateArea(areaId, (a) => ({ ...a, scanNote }), now);
}

// 2026-10-19 (Owner item 31): a photo-checklist shot answered "does not apply / not here", or reopened.
export function setShotSkippedAction(areaId: string, shotKey: string, skipped: boolean, now: number = Date.now()) {
  return updateArea(
    areaId,
    (a) => {
      const current = new Set(a.shotSkips ?? []);
      if (skipped) current.add(shotKey);
      else current.delete(shotKey);
      return { ...a, shotSkips: [...current] };
    },
    now
  );
}

// Area Scan "Skip AI / Start Inspecting" ↔ back to the AI options (Authority §7.2 MANUAL / SKIP).
export function setAreaAiSkippedAction(areaId: string, aiSkipped: boolean, now: number = Date.now()) {
  return updateArea(areaId, (a) => ({ ...a, aiSkipped }), now);
}

// Move an area to another floor (or out of the floors, null). The only post-creation write of
// Area.floor (Authority §6.2); the area's name, rows and records are untouched.
export function moveAreaToFloorAction(areaId: string, floor: number | null, now: number = Date.now()) {
  return updateArea(areaId, (a) => ({ ...a, floor }), now);
}

// Reorder within the area's own section (its floor, or its catalog group): swap sortOrder with the
// neighbour in the given direction. Returns false (no write) at the edges.
export async function reorderAreaAction(areaId: string, direction: 'up' | 'down', now: number = Date.now()): Promise<boolean> {
  const state = inspectionStore.get();
  const area = state.areas.find((a) => a.id === areaId);
  if (!area) return false;
  assertInspectionUnlocked(area.inspectionId);
  const section = areaSectionOf(area).order;
  const siblings = areasOf(state, area.inspectionId).filter((a) => areaSectionOf(a).order === section);
  const index = siblings.findIndex((a) => a.id === areaId);
  const target = direction === 'up' ? index - 1 : index + 1;
  if (index < 0 || target < 0 || target >= siblings.length) return false;
  const other = siblings[target];
  const m = emptyMutation();
  m.areas.push({ ...area, sortOrder: other.sortOrder, updatedAt: now }, { ...other, sortOrder: area.sortOrder, updatedAt: now });
  reconcile(m, area.inspectionId, now);
  await commit(m);
  return true;
}

export async function completeAreaAction(
  areaId: string,
  now: number = Date.now()
): Promise<{ ok: true } | { ok: false; blockers: V2ChecklistItem[] }> {
  const state = inspectionStore.get();
  const blockers = areaCompletionBlockers(itemsOf(state, areaId));
  if (blockers.length > 0) return { ok: false, blockers };
  await updateArea(areaId, (a) => ({ ...a, completedAt: now }), now);
  return { ok: true };
}

export function reopenAreaAction(areaId: string, now: number = Date.now()) {
  return updateArea(areaId, (a) => ({ ...a, completedAt: null }), now);
}

// Removal of an area record with its checklist rows, prepared for ONE transaction. The
// data guard (an area with any inspection data is never deleted) lives in
// src/state/deleteArea.ts, which is the only caller.
export function prepareAreaRemoval(areaId: string, now: number): { writes: RecordWrite[]; deletes: RecordDelete[]; apply: () => void } | null {
  const state = inspectionStore.get();
  const area = state.areas.find((a) => a.id === areaId);
  if (!area) return null;
  assertInspectionUnlocked(area.inspectionId);
  const inspection = state.inspections.find((i) => i.id === area.inspectionId);
  const itemIds = state.items.filter((i) => i.areaId === areaId).map((i) => i.id);
  const touched = inspection ? { ...inspection, updatedAt: now } : null;
  return {
    writes: touched ? [{ store: 'inspections', value: touched }] : [],
    deletes: [{ store: 'areas', key: areaId }, ...itemIds.map((id) => ({ store: 'items' as const, key: id }))],
    apply: () =>
      inspectionStore.set((prev) => ({
        ...prev,
        inspections: touched ? sortInspections(replaceById(prev.inspections, [touched])) : prev.inspections,
        areas: prev.areas.filter((a) => a.id !== areaId),
        items: prev.items.filter((i) => i.areaId !== areaId)
      }))
  };
}

// ---------- item actions ----------

export async function addCustomItemAction(
  inspectionId: string,
  areaId: string,
  title: string,
  hint: string = '',
  now: number = Date.now()
): Promise<V2ChecklistItem | null> {
  assertInspectionUnlocked(inspectionId);
  const trimmed = title.trim();
  if (trimmed.length === 0) return null;
  const existing = itemsOf(inspectionStore.get(), areaId);
  const sortOrder = existing.length > 0 ? Math.max(...existing.map((i) => i.sortOrder)) + 1 : 0;
  const item = createItem(inspectionId, areaId, { templateId: null, title: trimmed, hint }, sortOrder, now);
  const m = emptyMutation();
  m.items.push(item);
  reconcile(m, inspectionId, now);
  await commit(m);
  return item;
}

// ---------- item writes ----------
//
// Final Owner Decision 2026-09-14 (CA-15 / D-CA-1): a checklist row carries no finding record. A Defect
// is the row's own result; the report derives the row's defect entry from the row at assembly time, so
// leaving Defect leaves nothing stale behind by construction — no removal gate, no confirmation.
async function updateItem(
  itemId: string,
  mutate: (item: V2ChecklistItem) => V2ChecklistItem,
  now: number
): Promise<V2ChecklistItem | null> {
  const current = inspectionStore.get().items.find((i) => i.id === itemId);
  if (!current) return null;
  assertInspectionUnlocked(current.inspectionId);
  const next = mutate(current);
  if (next === current) return current;
  const m = emptyMutation();
  m.items.push(next);
  reconcile(m, next.inspectionId, now);
  await commit(m);
  return next;
}

export function setItemStatusAction(itemId: string, status: ItemStatus, now: number = Date.now()) {
  return updateItem(itemId, (item) => withItemStatus(item, status, now), now);
}

// 2026-09-13 ratification §4, on the row since 2026-09-14: the inspector confirms the structural fact
// that turns the row's referral into a Professional Engineer (PE). Never set by the AI.
export function setItemStructuralConcernAction(itemId: string, structuralConcern: boolean, now: number = Date.now()) {
  return updateItem(itemId, (item) => withItemStructuralConcern(item, structuralConcern, now), now);
}

// 2026-10-19 (Owner decision list item 16): the inspector chooses the defect tier of a row. null returns
// the row to the suggested tier (src/domain/severity.ts).
export function setItemSeverityAction(itemId: string, severity: DefectSeverity | null, now: number = Date.now()) {
  return updateItem(itemId, (item) => (item.severity === severity ? item : { ...item, severity, updatedAt: now }), now);
}

// 2026-09-13 (TW-3): the inspector records whether an operational test was performed (smoke / CO test
// button; since 2026-09-29 every check whose sentence depends on it — src/report/rowWording.ts
// checkTypeRecordsTest). The report sentence follows this fact; nothing infers it.
export function setItemOperationalTestAction(itemId: string, performed: boolean, now: number = Date.now()) {
  return updateItem(itemId, (item) => (item.operationalTestPerformed === performed ? item : { ...item, operationalTestPerformed: performed, updatedAt: now }), now);
}

export type ItemResultOutcome =
  | { ok: true; item: V2ChecklistItem | null }
  // NY F1 R3 narrow repair (F-R3-04): the new result cannot carry the recorded parts not observed —
  // the UI asks and calls again with confirmPortionRemoval = true; nothing is written until then.
  | { ok: false; reason: 'needsPortionConfirmation'; portions: LimitedPortion[] };

// The explicit answers a transition may already carry (each gate asks once; the UI passes them back).
export interface ItemResultConfirmations {
  confirmPortionRemoval?: boolean;
}

// The inspector-facing transition. The inspector is the only one who ever calls it (CA-11): no AI
// path reaches a result. Leaving Defect deletes nothing — the row's defect entry is derived — so the
// only gate left is the recorded parts not observed (F-R3-04).
export async function changeItemResultAction(
  itemId: string,
  status: ItemStatus,
  options: ItemResultConfirmations & { unable?: { reasonCode: UnableReason; note: string } } = {},
  now: number = Date.now()
): Promise<ItemResultOutcome> {
  const current = inspectionStore.get().items.find((i) => i.id === itemId);
  if (!current) return { ok: true, item: null };
  // F-R3-04: a whole-row Unable or N/A has no partially observed portion (OD-5) — the recorded parts
  // go with it, but never silently: the transition is refused until the inspector confirms it.
  if (!canCarryLimitedPortions(status) && current.limitedPortions.length > 0 && !options.confirmPortionRemoval) {
    return { ok: false, reason: 'needsPortionConfirmation', portions: current.limitedPortions };
  }
  const item =
    status === 'unableToInspect' && options.unable
      ? await setItemUnableAction(itemId, options.unable.reasonCode, options.unable.note, now)
      : await setItemStatusAction(itemId, status, now);
  return { ok: true, item };
}

export function setItemReasonAction(itemId: string, reason: string, now: number = Date.now()) {
  return updateItem(itemId, (item) => withItemReason(item, reason, now), now);
}

export function setItemReasonCodeAction(itemId: string, reasonCode: UnableReason | null, now: number = Date.now()) {
  return updateItem(itemId, (item) => withItemReasonCode(item, reasonCode, now), now);
}

// Unable to Inspect in one write: the result, the required reason and the optional note
// (the quick reason sheet — task book §15).
export function setItemUnableAction(itemId: string, reasonCode: UnableReason, note: string, now: number = Date.now()) {
  return updateItem(itemId, (item) => withItemReason(withItemReasonCode(withItemStatus(item, 'unableToInspect', now), reasonCode, now), note, now), now);
}

// NY F1 Round 3 (OD-5 = A): a limited portion on the existing row. The result of the observed portion,
// the descriptors, the evidence and the notes are untouched; no row and no task is created.
export function addLimitedPortionAction(itemId: string, portion: LimitedPortion, now: number = Date.now()) {
  return updateItem(itemId, (item) => withLimitedPortion(item, portion, now), now);
}

export function replaceLimitedPortionAction(itemId: string, index: number, portion: LimitedPortion, now: number = Date.now()) {
  return updateItem(itemId, (item) => withLimitedPortionAt(item, index, portion, now), now);
}

export function removeLimitedPortionAction(itemId: string, index: number, now: number = Date.now()) {
  return updateItem(itemId, (item) => withoutLimitedPortion(item, index, now), now);
}

export function setItemNoteAction(itemId: string, note: string, now: number = Date.now()) {
  return updateItem(itemId, (item) => withItemNote(item, note, now), now);
}

// 2026-09-14: the report sentence written for the row (AI or inspector), or null to clear it.
export function setItemReportSentenceAction(itemId: string, sentence: ItemReportSentence | null, now: number = Date.now()) {
  return updateItem(itemId, (item) => withItemReportSentence(item, sentence, now), now);
}

// ⑥ (CA-13 / §15.7; Owner device test 2026-09-21): the inspector edits the report sentence — in
// English or, since 2026-09-21, in Chinese. Saved as inspector-authored under the row's current result,
// in the language it was written in; the OTHER language stays as it was and is shown as not synced
// until the AI renders the inspector's words again or the inspector edits that side too. Clearing the
// text returns the row to the library sentence in both languages.
// `otherStanding` is the OTHER language's stored sentence while it still stands ('' when the sheet was
// showing the library sentence there): a stale AI sentence is never carried forward under the
// inspector's name. Omitted = whatever is stored (the English-only callers before 2026-09-21).
export function setItemReportSentenceByInspectorAction(itemId: string, text: string, basis: string, now: number = Date.now(), language: 'en' | 'zh' = 'en', otherStanding?: string) {
  return updateItem(
    itemId,
    (item) => {
      const written = text.trim();
      if (written.length === 0) return withItemReportSentence(item, null, now);
      const stored = item.reportSentence;
      const other = otherStanding !== undefined ? otherStanding.trim() : language === 'en' ? (stored?.zh ?? '') : (stored?.en ?? '');
      if (stored?.source === 'inspector' && (language === 'en' ? stored.en : stored.zh) === written && (stored.edited ?? 'en') === language) return item;
      return withItemReportSentence(item, { en: language === 'en' ? written : other, zh: language === 'zh' ? written : other, source: 'inspector', basis, at: now, edited: language }, now);
    },
    now
  );
}

// The INSPECTOR links a photo to the row (any earlier removal of it is forgotten, CA-7).
export function linkEvidenceToItemAction(itemId: string, evidenceId: string, now: number = Date.now()) {
  return updateItem(itemId, (item) => withEvidenceLink(item, evidenceId, now), now);
}

// The INSPECTOR removes a photo from the row: an override no automatic link ever undoes (CA-7 / §9.2).
export function unlinkEvidenceFromItemAction(itemId: string, evidenceId: string, now: number = Date.now()) {
  return updateItem(itemId, (item) => withoutEvidenceLink(item, evidenceId, now), now);
}

// Used by the evidence flow to include item link updates inside its own transaction.
export function prepareItemEvidenceLink(itemId: string, evidenceId: string, now: number): { write: RecordWrite | null; apply: () => void } {
  const current = inspectionStore.get().items.find((i) => i.id === itemId);
  if (!current) return { write: null, apply: () => undefined };
  const next = withEvidenceLink(current, evidenceId, now);
  if (next === current) return { write: null, apply: () => undefined };
  return {
    write: { store: 'items', value: next },
    apply: () => inspectionStore.set((prev) => ({ ...prev, items: replaceById(prev.items, [next]) }))
  };
}

// The AI's photo PLACEMENTS (§9.1; 2026-10-27 「AI 放的」, linked at once and marked), prepared for the AI
// store's own transaction: every removal the inspector made on the row is honoured (withEvidenceSuggestions
// skips excluded photos) and a photo linked by hand keeps its link unmarked. The inspector undoes a wrong
// placement with dismissEvidenceSuggestionAction (「不是这项」).
export function prepareItemEvidenceSuggestions(links: ReadonlyMap<string, readonly string[]>, now: number): { writes: RecordWrite[]; apply: () => void } {
  const affected: V2ChecklistItem[] = [];
  const state = inspectionStore.get();
  for (const [itemId, evidenceIds] of links) {
    const current = state.items.find((i) => i.id === itemId);
    if (!current) continue;
    const next = withEvidenceSuggestions(current, evidenceIds, now);
    if (next !== current) affected.push(next);
  }
  return {
    writes: affected.map((value) => ({ store: 'items' as const, value })),
    apply: () => {
      if (affected.length > 0) inspectionStore.set((prev) => ({ ...prev, items: replaceById(prev.items, affected) }));
    }
  };
}

// The INSPECTOR confirms an AI-placed photo: the link stays, the 「AI 放的」 mark goes (the same link the
// inspector makes by hand — any earlier removal is forgotten).
export function confirmEvidenceSuggestionAction(itemId: string, evidenceId: string, now: number = Date.now()) {
  return updateItem(itemId, (item) => (item.evidenceSuggestions.includes(evidenceId) ? withEvidenceLink(item, evidenceId, now) : item), now);
}

// The INSPECTOR says the AI-placed photo is not this row's: the link goes and the removal override is
// recorded, so no later run places it again (CA-7).
export function dismissEvidenceSuggestionAction(itemId: string, evidenceId: string, now: number = Date.now()) {
  return updateItem(itemId, (item) => withEvidenceSuggestionDismissed(item, evidenceId, now), now);
}

// 2026-10-27 (Owner, device walkthrough: 「AI 判缺陷即行结果默认缺陷」): a row the inspector has NOT answered yet
// (notChecked) whose AI observation concluded a defect is prefilled as 缺陷, inside the AI store's own
// transaction. A row with any answer already — 无异常, 缺陷, 无法检查, 不适用 — is never touched: the inspector's
// result still rules (CA-11), and the item sheet asks before 缺陷 is changed to 无异常 on such a row. The
// suggested tier (src/domain/severity.ts) prints until the inspector chooses one. This is the ONLY path
// by which an AI answer reaches a result (tests/guards/aiEntryPoints.guard.test.ts).
// `pending` = rows the same transaction already rewrote (the photo placements): the default is computed on
// top of that version, so the row is written once with both.
export function prepareAiDefectDefaults(itemIds: ReadonlySet<string>, now: number, pending: readonly V2ChecklistItem[] = []): { writes: RecordWrite[]; apply: () => void } {
  const affected: V2ChecklistItem[] = [];
  for (const stored of inspectionStore.get().items) {
    const current = pending.find((p) => p.id === stored.id) ?? stored;
    if (!itemIds.has(current.id) || current.status !== 'notChecked') continue;
    affected.push(withItemStatus(current, 'issueFound', now));
  }
  return {
    writes: affected.map((value) => ({ store: 'items' as const, value })),
    apply: () => {
      if (affected.length > 0) inspectionStore.set((prev) => ({ ...prev, items: replaceById(prev.items, affected) }));
    }
  };
}

// A photo record deleted from the app: its link, any override and any proposal about it go from every row.
export function prepareItemEvidenceUnlinks(evidenceId: string, now: number): { writes: RecordWrite[]; apply: () => void } {
  const affected = inspectionStore
    .get()
    .items.filter((i) => i.evidenceIds.includes(evidenceId) || i.evidenceExclusions.includes(evidenceId) || i.evidenceSuggestions.includes(evidenceId))
    .map((i) => withoutEvidenceEntirely(i, evidenceId, now));
  return {
    writes: affected.map((value) => ({ store: 'items' as const, value })),
    apply: () => {
      if (affected.length > 0) inspectionStore.set((prev) => ({ ...prev, items: replaceById(prev.items, affected) }));
    }
  };
}
